import { CommunicationRoleEnum, CommunicationTypeEnum, CountryIsoCodeEnum, PartySubTypeEnum, PartyTypeEnum, } from '@zurich/api/party';
import { createDate, isBefore } from '@zurich/common/forms';
import { createStringEnumUtils, UnknownType } from '@zurich/common/utils';
import { Party, PartySalutation } from './notMigratedYet';
import { partyCommunicationChannelUtils } from './partyCommunicationChannelUtils';
export const partyUtils = {
    isSubTypePrivatPerson,
    isSubTypeEinzelunternehmer,
    isSubTypeUnternehmen,
    isSubTypeVerein,
    isPerson,
    isLegalEntity,
    getLegalEntity,
    getPerson,
    types,
    subTypes,
    isPrivatPerson,
    isEinzelunternehmer,
    isVerein,
    isUnternehmen,
    getName,
    getNameShort,
    getInitials,
    getMainEmail,
    getAllEmails,
    getMainFullPhone,
    getAddress,
};
function makePartyRow(entry, t) {
    return {
        label: t(`party:label.${entry.i18nKey}`),
        value: entry.value,
    };
}
export function makePartyRows(entries, t) {
    return entries.map((entry) => makePartyRow(entry, t)).filter((r) => r.value);
}
function isLegalEntity(party) {
    return party.partyType === PartyTypeEnum.J;
}
function isPerson(party) {
    return party.partyType === PartyTypeEnum.N;
}
function getLegalEntity(party) {
    // TODO: what should we do if type/subType is null?
    if (!isLegalEntity(party))
        throw new Error('PartyType is not LegalEntity');
    return party;
}
function getPerson(party) {
    // TODO: what should we do if type/subType is null?
    if (!isPerson(party))
        throw new Error('PartyType is not Person');
    return party;
}
function types(party) {
    return {
        isLegalEntity: isLegalEntity(party),
        isPerson: isPerson(party),
    };
}
function isSubTypePrivatPerson(partySubType) {
    return partySubType === 'P';
}
function isPrivatPerson(party) {
    return isPerson(party) && isSubTypePrivatPerson(party.partySubType);
}
function isSubTypeEinzelunternehmer(partySubType) {
    return partySubType === 'E';
}
function isEinzelunternehmer(party) {
    return isPerson(party) && isSubTypeEinzelunternehmer(party.partySubType);
}
function isSubTypeVerein(partySubType) {
    return partySubType === 'V';
}
function isVerein(party) {
    return isLegalEntity(party) && isSubTypeVerein(party.partySubType);
}
function isSubTypeUnternehmen(partySubType) {
    return partySubType === 'U';
}
function isUnternehmen(party) {
    return isLegalEntity(party) && isSubTypeUnternehmen(party.partySubType);
}
function subTypes(party) {
    return {
        isPrivatperson: isPrivatPerson(party),
        isEinzelunternehmer: isEinzelunternehmer(party),
        isVerein: isVerein(party),
        isUnternehmen: isUnternehmen(party),
    };
}
function getName(party, t) {
    if (isPerson(party)) {
        const _party = getPerson(party);
        return [
            _party.salutation &&
                PartySalutation.getTranslatedValue(_party.salutation, t), // TODO: move to utils pattern
            // field disabled for now
            // _party.degreeAwarded,
            _party.academicDegreePrefix,
            _party.firstName,
            _party.lastName,
            _party.nameAffix,
            _party.academicDegreePostfix,
        ]
            .filter((v) => !!v)
            .join(' ');
    }
    if (isLegalEntity(party)) {
        return getLegalEntity(party).organizationName;
    }
}
function getNameShort(party) {
    if (isPerson(party)) {
        const _party = getPerson(party);
        return [_party.firstName, _party.lastName, _party.nameAffix]
            .filter((v) => !!v)
            .join(' ');
    }
    if (isLegalEntity(party)) {
        return getLegalEntity(party).organizationName;
    }
}
function getInitials(party) {
    if (Party.isPerson(party)) {
        const person = Party.getPerson(party);
        return `${person?.firstName?.charAt(0)}${person?.lastName?.charAt(0)}`;
    }
    if (Party.isLegalEntity(party)) {
        return Party.getLegalEntity(party).organizationName?.charAt(0);
    }
    return '?';
}
function getMainEmail(party) {
    const mainEmailChannel = party?.extendedPartyData?.communicationChannels?.find((cc) => cc.communicationChannelType === CommunicationTypeEnum.EMAIL &&
        cc.communicationRole === CommunicationRoleEnum.EH);
    return mainEmailChannel &&
        partyCommunicationChannelUtils.isPartyEmailAddress(mainEmailChannel)
        ? mainEmailChannel.emailAddress
        : undefined;
}
function getAllEmails(party) {
    return (party?.extendedPartyData?.communicationChannels
        ?.filter((cc) => partyCommunicationChannelUtils.isPartyEmailAddress(cc))
        .map((partyEmailAddress) => partyCommunicationChannelUtils.getPartyEmailAddress(partyEmailAddress)
        .emailAddress) ?? []);
}
function getMainFullPhone(party) {
    const mainPhoneChannel = party?.extendedPartyData?.communicationChannels?.find((cc) => cc.communicationChannelType === CommunicationTypeEnum.TELEPHONE &&
        cc.communicationRole === CommunicationRoleEnum.TH);
    return mainPhoneChannel &&
        partyCommunicationChannelUtils.isPartyPhoneNumber(mainPhoneChannel)
        ? mainPhoneChannel.fullPhoneNumber
        : undefined;
}
function getAddress(party) {
    if (!party) {
        return undefined;
    }
    const address = party?.extendedPartyData?.communicationChannels?.find((cc) => cc.communicationChannelType === CommunicationTypeEnum.GEO &&
        (cc.communicationRole === CommunicationRoleEnum.HWS ||
            cc.communicationRole === CommunicationRoleEnum.FIRM));
    if (address && partyCommunicationChannelUtils.isPartyGeoAddress(address)) {
        const firstPart = `${address.street} ${[
            address.houseNumber,
            address.staircase,
            address.doorNumber,
        ]
            .filter((no) => !!no)
            .join('/')}`;
        return `${firstPart}, ${address.postalCode} ${address.city}`;
    }
    return undefined;
}
// TODO: this should be somewhere else!
export function getDegreeValue(options, value) {
    // get all valid seperate options
    const filtered = options.filter((option) => value.includes(option.value));
    // merge string from server side into array to allow that value
    // to be the first chip containing the whole value
    return value[0]
        ? options.find((option) => value[0] === option.value)
            ? filtered
            : [
                {
                    label: value[0],
                    value: value[0],
                },
                ...filtered,
            ]
        : [];
}
export const isTooOldForCustomerPortalRegistration = (dateOfBirth) => dateOfBirth
    ? isBefore(createDate(dateOfBirth), createDate('1960-01-01'))
    : false;
export const NEW_HOUSEHOLD_NO = 'newHouseholdNo';
export const prioritizedCountryUtils = createStringEnumUtils(CountryIsoCodeEnum, {
    unknownType: UnknownType.STRING,
    translationPrefix: 'party:countries',
    includeOptions: [
        CountryIsoCodeEnum.AT,
        CountryIsoCodeEnum.DE,
        CountryIsoCodeEnum.CH,
    ],
});
export const partySubTypeUtils = (excludedOptionsPartySubType) => createStringEnumUtils(PartySubTypeEnum, {
    unknownType: UnknownType.STRING,
    translationPrefix: 'party:subType',
    excludeOptions: excludedOptionsPartySubType,
});
export const shortenedAddressCountryUtils = createStringEnumUtils(CountryIsoCodeEnum, {
    unknownType: UnknownType.STRING,
    translationPrefix: 'party:countries',
    includeOptions: [
        CountryIsoCodeEnum.AL,
        CountryIsoCodeEnum.AD,
        CountryIsoCodeEnum.AZ,
        CountryIsoCodeEnum.BE,
        CountryIsoCodeEnum.BG,
        CountryIsoCodeEnum.BA,
        CountryIsoCodeEnum.BY,
        CountryIsoCodeEnum.CY,
        CountryIsoCodeEnum.CZ,
        CountryIsoCodeEnum.DK,
        CountryIsoCodeEnum.ES,
        CountryIsoCodeEnum.EE,
        CountryIsoCodeEnum.FR,
        CountryIsoCodeEnum.FI,
        CountryIsoCodeEnum.LI,
        CountryIsoCodeEnum.GB,
        CountryIsoCodeEnum.GR,
        CountryIsoCodeEnum.HU,
        CountryIsoCodeEnum.HR,
        CountryIsoCodeEnum.IT,
        CountryIsoCodeEnum.IE,
        CountryIsoCodeEnum.IS,
        CountryIsoCodeEnum.LU,
        CountryIsoCodeEnum.LT,
        CountryIsoCodeEnum.LV,
        CountryIsoCodeEnum.MT,
        CountryIsoCodeEnum.MC,
        CountryIsoCodeEnum.MD,
        CountryIsoCodeEnum.MK,
        CountryIsoCodeEnum.ME,
        CountryIsoCodeEnum.NO,
        CountryIsoCodeEnum.NL,
        CountryIsoCodeEnum.PT,
        CountryIsoCodeEnum.PL,
        CountryIsoCodeEnum.RO,
        CountryIsoCodeEnum.RU,
        CountryIsoCodeEnum.SE,
        CountryIsoCodeEnum.SK,
        CountryIsoCodeEnum.SI,
        CountryIsoCodeEnum.RS,
        CountryIsoCodeEnum.TR,
        CountryIsoCodeEnum.UA,
    ],
});
export function createPartnerSearchKeyValuePair(id) {
    return id.charAt(0).toUpperCase() === 'P'
        ? { key: '_id', value: id }
        : { key: 'legacyPartnerNr', value: id };
}
